import { useEffect, useState, type ReactNode } from "react";
import {
  X,
  Store,
  ShieldCheck,
  Zap,
  ChevronDown,
  ChevronRight,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Trash2,
  KeyRound,
  PlayCircle,
  FileText,
  Search,
  HelpCircle,
  RefreshCw,
  Pencil,
  ExternalLink,
  Copy,
  Check,
} from "lucide-react";
import toast from "react-hot-toast";
import { Language, dictionary } from "../../../utils/dictionary";
import { INTEGRATIONS_UI_CONFIG } from "../config/integrations.config";
import {
  getShopifyConnections,
  deleteShopifyConnection,
  upsertShopifyConnection,
  updateShopifyConnection,
  startShopifyConnectionOAuth,
  type ShopifyConnectionInterface,
} from "../../../services/integrations/integrationsService";
import { handleApi } from "../../../utils/handleApi";

const VIDEO_TUTORIAL_URL = "https://youtu.be/gU-xnk-dEjM?si=LOlmFwpCcEDzbRtJ";
const STEP_BY_STEP_URL = "https://fluxi.s3.amazonaws.com/prod/assets/documentation/shopify-integration-token-v3";

const SHOPIFY_LOGO = INTEGRATIONS_UI_CONFIG.shopify.logoSrc;
const FLUXI_LOGO = "https://fluxi.s3.amazonaws.com/prod/assets/fluxi/fluxi-logo.svg";

const SEARCH_THRESHOLD = 6;

interface ModalShopifyConnectionsProps {
  setModalOpen: (open: boolean) => void;
  language: Language;
}

type FormMode = "closed" | "add" | "edit";

const byName = (a: ShopifyConnectionInterface, b: ShopifyConnectionInterface) =>
  (a.label?.trim() || a.url_store).localeCompare(b.label?.trim() || b.url_store, undefined, {
    sensitivity: "base",
  });

const errorTrigger = (error: unknown) =>
  (error as { response?: { data?: { trigger?: string } } })?.response?.data?.trigger;

const ModalShopifyConnections = ({ setModalOpen, language }: ModalShopifyConnectionsProps) => {
  const [connections, setConnections] = useState<ShopifyConnectionInterface[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [search, setSearch] = useState("");

  const [formMode, setFormMode] = useState<FormMode>("closed");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formUrl, setFormUrl] = useState("");
  const [formLabel, setFormLabel] = useState("");
  const [formClientId, setFormClientId] = useState("");
  const [formClientSecret, setFormClientSecret] = useState("");
  const [formToken, setFormToken] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [oauth, setOauth] = useState<{ connectionId: number; url: string } | null>(null);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [verifyResult, setVerifyResult] = useState<"idle" | "not-yet" | "connected">("idle");
  const [confirmDelete, setConfirmDelete] = useState<ShopifyConnectionInterface | null>(null);

  const q = search.trim().toLowerCase();
  const filteredConnections = q
    ? connections.filter(
        (c) =>
          (c.label || "").toLowerCase().includes(q) ||
          (c.url_store || "").toLowerCase().includes(q)
      )
    : connections;

  const needsAttention = filteredConnections.filter((c) => !c.connected).sort(byName);
  const active = filteredConnections.filter((c) => c.connected).sort(byName);

  const total = connections.length;
  const activeCount = connections.filter((c) => c.connected).length;
  const isEmpty = !loading && total === 0;
  const showSearch = total >= SEARCH_THRESHOLD;

  // Identidad por app (client_id): si el Client ID que escribe ya existe, se actualizará esa
  // conexión (no se crea otra). Misma tienda con otra app = conexión nueva (sin aviso).
  const duplicateConnection =
    formMode === "add" && formClientId.trim()
      ? connections.find((c) => !!c.client_id && c.client_id === formClientId.trim()) ?? null
      : null;

  const loadConnections = async (): Promise<ShopifyConnectionInterface[]> => {
    setLoading(true);
    const { res, error } = await handleApi(getShopifyConnections(), false);
    setLoading(false);
    const list = error || !res || !Array.isArray(res) ? [] : res;
    setConnections(list);
    return list;
  };

  useEffect(() => {
    loadConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    const { error } = await handleApi(deleteShopifyConnection(id), false);
    setDeletingId(null);
    if (error) {
      toast.error(dictionary["integration-error"][language]);
      return;
    }
    setConnections((prev) => prev.filter((c) => c.id !== id));
    toast.success(dictionary["integration-canceled"][language]);
  };

  const resetForm = () => {
    setFormUrl("");
    setFormLabel("");
    setFormClientId("");
    setFormClientSecret("");
    setFormToken("");
    setShowAdvanced(false);
    setOauth(null);
    setCopiedUrl(false);
    setVerifyResult("idle");
    setSubmitting(false);
  };

  const handleCopyUrl = async () => {
    if (!oauth?.url) return;
    try {
      await navigator.clipboard.writeText(oauth.url);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch {
      toast.error(dictionary["shopify-connections-oauth-copy-error"][language]);
    }
  };

  const openAdd = () => {
    resetForm();
    setEditingId(null);
    setFormMode("add");
  };

  const openEdit = (c: ShopifyConnectionInterface) => {
    resetForm();
    setEditingId(c.id);
    setFormUrl(c.url_store || "");
    setFormLabel(c.label || "");
    setFormMode("edit");
  };

  const closeForm = () => {
    setFormMode("closed");
    setEditingId(null);
    resetForm();
  };

  const toastTrigger = (error: unknown, fallbackKey: string) => {
    const trigger = errorTrigger(error);
    toast.error(
      trigger && dictionary[trigger]?.[language]
        ? dictionary[trigger][language]
        : dictionary[fallbackKey][language]
    );
  };

  // Paso 1 OAuth: registra/actualiza credenciales y prepara la autorización (sin abrirla automáticamente).
  const handleConnectOAuth = async () => {
    const url = formUrl.trim();
    if (!url) {
      toast.error(dictionary["shopify-connections-add-required"][language]);
      return;
    }
    if (formMode === "add" && (!formClientId.trim() || !formClientSecret.trim())) {
      toast.error(dictionary["shopify-connections-add-required"][language]);
      return;
    }
    setSubmitting(true);
    const { res, error } = await handleApi(
      startShopifyConnectionOAuth(
        {
          url_store: url,
          client_id: formClientId.trim() || undefined,
          client_secret: formClientSecret.trim() || undefined,
          label: formLabel.trim() || null,
        },
        editingId ?? undefined
      ),
      false
    );
    setSubmitting(false);
    if (error || !res) {
      toastTrigger(error, "shopify-connections-add-error");
      return;
    }
    setOauth({ connectionId: res.connection_id, url: res.authorization_url });
    setVerifyResult("idle");
  };

  // Abre la autorización de Shopify en una pestaña nueva (acción explícita del usuario).
  const handleOpenAuth = () => {
    if (!oauth?.url) return;
    window.open(oauth.url, "_blank", "noopener,noreferrer");
  };

  // Paso 2 OAuth: tras autorizar en Shopify, valida que la conexión quedó conectada.
  const handleValidateOAuth = async () => {
    if (!oauth) return;
    setSubmitting(true);
    const list = await loadConnections();
    setSubmitting(false);
    const conn = list.find((c) => c.id === oauth.connectionId);
    setVerifyResult(conn?.connected ? "connected" : "not-yet");
  };

  // Opción avanzada: pegar Admin API token directamente (sin OAuth).
  const handleSaveToken = async () => {
    const url = formUrl.trim();
    const token = formToken.trim();
    if (!url || (formMode === "add" && !token)) {
      toast.error(dictionary["shopify-connections-add-required"][language]);
      return;
    }
    setSubmitting(true);
    const payload = { label: formLabel.trim() || null, url_store: url, token: token || null };
    const req =
      formMode === "edit" && editingId != null
        ? updateShopifyConnection(editingId, payload)
        : upsertShopifyConnection({ label: payload.label, url_store: url, token });
    const { res, error } = await handleApi(req, false);
    setSubmitting(false);
    if (error || !res) {
      toastTrigger(error, "shopify-connections-add-error");
      return;
    }
    toast.success(
      formMode === "edit"
        ? dictionary["shopify-connections-revalidated"][language]
        : dictionary["shopify-connections-add-success"][language]
    );
    closeForm();
    loadConnections();
  };

  const countLabel = `${total} ${
    total === 1
      ? dictionary["shopify-connections-count-word-one"][language]
      : dictionary["shopify-connections-count-word"][language]
  } · ${activeCount} ${dictionary["shopify-connections-active-word"][language]}`;

  const renderForm = (keyPrefix: string) => (
    <div key={keyPrefix} className="mt-4 flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
      {oauth ? (
        <div className="flex flex-col gap-4">
          {verifyResult === "connected" ? (
            /* Verificación: la integración quedó montada */
            <div className="flex items-center gap-3 rounded-lg border border-[#bbf7d0] bg-[#f0fdf4] p-4">
              <CheckCircle2 className="h-6 w-6 shrink-0 text-[#22a06b]" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-[#166534]">
                  {dictionary["shopify-connections-oauth-connected"][language]}
                </p>
                <p className="text-xs text-[#15803d]">
                  {dictionary["shopify-connections-oauth-verified-sub"][language]}
                </p>
              </div>
              <button
                type="button"
                onClick={closeForm}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#22a06b] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1c8a5b]"
              >
                {dictionary["shopify-connections-oauth-done"][language]}
              </button>
            </div>
          ) : (
            <>
              {/* Paso 1: abrir y aprobar la autorización en Shopify */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#22a06b] text-[11px] font-bold text-white">1</span>
                  <p className="text-sm font-medium text-gray-800">
                    {dictionary["shopify-connections-oauth-step1"][language]}
                  </p>
                </div>
                {/* Una fila: link visible + Copiar + Abrir */}
                <div className="flex items-stretch gap-2">
                  <input
                    type="text"
                    readOnly
                    value={oauth.url}
                    onFocus={(e) => e.currentTarget.select()}
                    className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 font-mono text-xs text-gray-700 focus:border-gray-400 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={handleCopyUrl}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    {copiedUrl ? <Check className="h-4 w-4 text-[#22a06b]" /> : <Copy className="h-4 w-4" />}
                    {copiedUrl
                      ? dictionary["shopify-connections-oauth-copied"][language]
                      : dictionary["shopify-connections-oauth-copy"][language]}
                  </button>
                  <button
                    type="button"
                    onClick={handleOpenAuth}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#22a06b] px-3 py-2 text-sm font-semibold text-white hover:bg-[#1c8a5b]"
                  >
                    <ExternalLink className="h-4 w-4" />
                    {dictionary["shopify-connections-oauth-open-short"][language]}
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  {dictionary["shopify-connections-oauth-copy-hint"][language]}
                </p>
              </div>

              {/* Paso 2: validar que la conexión quedó montada */}
              <div className="flex flex-col gap-2 border-t border-gray-200 pt-3">
                <div className="flex items-center gap-2">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#22a06b] text-[11px] font-bold text-white">2</span>
                  <p className="text-sm font-medium text-gray-800">
                    {dictionary["shopify-connections-oauth-step2"][language]}
                  </p>
                </div>
                {verifyResult === "not-yet" && (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                    <p className="text-xs text-amber-700">{dictionary["shopify-connections-oauth-not-yet"][language]}</p>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleValidateOAuth}
                    disabled={submitting}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-[#22a06b] bg-white px-4 py-2 text-sm font-semibold text-[#22a06b] hover:bg-[#f0fdf4] disabled:opacity-60"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    {dictionary["shopify-connections-oauth-validate"][language]}
                  </button>
                  <button
                    type="button"
                    onClick={closeForm}
                    className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
                  >
                    {dictionary["shopify-connections-cancel"][language]}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">
              {dictionary["shopify-connections-add-label"][language]}
            </label>
            <input
              type="text"
              value={formLabel}
              onChange={(e) => setFormLabel(e.target.value)}
              placeholder={dictionary["shopify-connections-add-label-ph"][language]}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-gray-400 focus:outline-none"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">
              {dictionary["shopify-connections-add-url"][language]}
            </label>
            <input
              type="text"
              value={formUrl}
              onChange={(e) => setFormUrl(e.target.value)}
              placeholder="mitienda.myshopify.com"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-gray-400 focus:outline-none"
            />
            {duplicateConnection && (
              <div className="mt-1 flex items-start gap-2 rounded-lg border border-blue-100 bg-blue-50 p-2.5">
                <RefreshCw className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                <p className="text-xs leading-5 text-blue-900">
                  {dictionary["shopify-connections-dup-registered-as"][language]}{" "}
                  <span className="font-semibold">
                    «{duplicateConnection.label?.trim() || duplicateConnection.url_store}»
                  </span>
                  . {dictionary["shopify-connections-dup-will-update"][language]}
                </p>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">
              {dictionary["shopify-connections-client-id"][language]}
            </label>
            <input
              type="text"
              value={formClientId}
              onChange={(e) => setFormClientId(e.target.value)}
              placeholder={
                formMode === "edit"
                  ? dictionary["shopify-connections-client-id-keep-ph"][language]
                  : ""
              }
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-gray-400 focus:outline-none"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">
              {dictionary["shopify-connections-client-secret"][language]}
            </label>
            <input
              type="password"
              value={formClientSecret}
              onChange={(e) => setFormClientSecret(e.target.value)}
              placeholder={
                formMode === "edit"
                  ? dictionary["shopify-connections-client-id-keep-ph"][language]
                  : ""
              }
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-mono focus:border-gray-400 focus:outline-none"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleConnectOAuth}
              disabled={submitting}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#22a06b] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1c8a5b] disabled:opacity-60"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : duplicateConnection ? (
                <RefreshCw className="h-4 w-4" />
              ) : (
                <Store className="h-4 w-4" />
              )}
              {duplicateConnection
                ? dictionary["shopify-connections-update-cta"][language]
                : dictionary["shopify-connections-connect-oauth"][language]}
            </button>
            <button
              type="button"
              onClick={closeForm}
              className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
            >
              {dictionary["shopify-connections-cancel"][language]}
            </button>
          </div>

          <div className="border-t border-gray-200 pt-3">
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"
            >
              {showAdvanced ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              {dictionary["shopify-connections-advanced-token"][language]}
            </button>
            {showAdvanced && (
              <div className="mt-2 flex flex-col gap-2">
                <input
                  type="password"
                  value={formToken}
                  onChange={(e) => setFormToken(e.target.value)}
                  placeholder={dictionary["shopify-connections-edit-token-ph"][language]}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-mono focus:border-gray-400 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={handleSaveToken}
                  disabled={submitting}
                  className="inline-flex w-fit items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                  {dictionary["shopify-connections-add-save"][language]}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  const renderConnection = (c: ShopifyConnectionInterface) => {
    if (formMode === "edit" && editingId === c.id) return renderForm(`edit-${c.id}`);

    const attentionReason = c.missing_scopes?.includes("write_orders")
      ? dictionary["shopify-connections-reconnect-missing-scope"][language]
      : dictionary["shopify-connections-reconnect-cant-validate"][language];
    const count = c.funnels_count ?? 0;
    const funnelsText =
      count > 0
        ? `${count} ${
            count === 1
              ? dictionary["shopify-connections-funnels-one"][language]
              : dictionary["shopify-connections-funnels-other"][language]
          }`
        : null;
    const subtitle = [c.url_store, c.connected ? null : attentionReason]
      .filter(Boolean)
      .join(" · ");

    return (
      <div
        key={c.id}
        className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3"
      >
        <div className="flex min-w-0 items-center gap-3">
          {c.connected ? (
            <CheckCircle2 className="h-5 w-5 shrink-0 text-[#22a06b]" />
          ) : (
            <AlertTriangle className="h-5 w-5 shrink-0 text-amber-500" />
          )}
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <p className="truncate text-sm font-medium text-gray-800">
                {c.label?.trim() || c.url_store}
              </p>
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                  c.connected ? "bg-[#f0fdf4] text-[#22a06b]" : "bg-amber-50 text-amber-600"
                }`}
              >
                {c.connected
                  ? dictionary["shopify-connections-connected"][language]
                  : dictionary["shopify-connections-needs-attention"][language]}
              </span>
              {funnelsText && (
                <span className="shrink-0 text-xs text-gray-400">· {funnelsText}</span>
              )}
            </div>
            <p className="truncate text-xs text-gray-500">{subtitle}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {!c.connected ? (
            <button
              type="button"
              onClick={() => openEdit(c)}
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-100"
              title={attentionReason}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              {dictionary["shopify-connections-reconnect"][language]}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => openEdit(c)}
              title={dictionary["shopify-connections-edit"][language]}
              className="rounded-md p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setConfirmDelete(c)}
            disabled={deletingId === c.id}
            className="rounded-md p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
            title={dictionary["shopify-connections-delete"][language]}
          >
            {deletingId === c.id ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
    );
  };

  const GroupHeading = ({ icon, label, count }: { icon: ReactNode; label: string; count: number }) => (
    <div className="mb-1 mt-1 flex items-center gap-1.5 px-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
      {icon}
      <span>{label}</span>
      <span className="font-normal normal-case text-gray-400">({count})</span>
    </div>
  );

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-[850px] max-h-[90vh] overflow-hidden flex flex-col md:flex-row relative">
        <button
          onClick={() => setModalOpen(false)}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 z-10 md:hidden"
        >
          <X size={24} />
        </button>

        {/* Left Side - Content (ÚNICA región scrollable) */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8 flex flex-col">
          {/* Header */}
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#dcfce7] bg-[#f0fdf4]">
              <img src={SHOPIFY_LOGO} alt="Shopify" className="h-6 w-6 object-contain" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-bold leading-tight text-gray-900">
                {dictionary["shopify-connections-title"][language]}
              </h2>
              {!isEmpty && !loading && (
                <p className="mt-0.5 text-xs text-gray-500">{countLabel}</p>
              )}
            </div>
          </div>

          {/* Intro (estilo Confío): descripción + beneficios + ayuda */}
          <>
              <p className="mb-6 text-sm leading-relaxed text-gray-600">
                {dictionary["shopify-connections-description"][language]}
              </p>
              <div className="mb-6 space-y-3">
                <div className="flex items-start gap-3">
                  <Store className="mt-0.5 h-5 w-5 shrink-0 text-[#22a06b]" />
                  <span className="text-sm text-gray-700">
                    {dictionary["shopify-connections-feature-register"][language]}
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#22a06b]" />
                  <span className="text-sm text-gray-700">
                    {dictionary["shopify-connections-feature-validate"][language]}
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <Zap className="mt-0.5 h-5 w-5 shrink-0 text-[#22a06b]" />
                  <span className="text-sm text-gray-700">
                    {dictionary["shopify-connections-feature-reuse"][language]}
                  </span>
                </div>
              </div>
              <div className="mb-5 flex flex-col gap-3 rounded-xl bg-blue-50 p-4 sm:flex-row sm:items-center">
                <div className="flex flex-1 items-center gap-3">
                  <HelpCircle className="h-6 w-6 shrink-0 text-blue-600" />
                  <p className="text-sm font-medium text-blue-900">
                    {dictionary["shopify-connections-help-title"][language]}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <a
                    href={VIDEO_TUTORIAL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-2 text-xs font-medium text-blue-700 ring-1 ring-blue-100 transition-colors hover:bg-blue-100"
                  >
                    <PlayCircle className="h-4 w-4" />
                    {dictionary["shopify-connections-video-short"][language]}
                  </a>
                  <a
                    href={STEP_BY_STEP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-2 text-xs font-medium text-blue-700 ring-1 ring-blue-100 transition-colors hover:bg-blue-100"
                  >
                    <FileText className="h-4 w-4" />
                    {dictionary["shopify-connections-pdf-short"][language]}
                  </a>
                </div>
              </div>
          </>

          {/* Buscador (solo con varias conexiones) */}
          {showSearch && (
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={dictionary["shopify-connections-search-ph"][language]}
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm focus:border-gray-400 focus:outline-none"
              />
            </div>
          )}

          {/* Lista de conexiones — scroll único con la columna */}
          {loading ? (
            <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-gray-200 bg-gray-50 py-6 text-sm text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              {dictionary["shopify-connections-loading"][language]}
            </div>
          ) : isEmpty ? (
            <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 py-6 text-center text-sm text-gray-400">
              {dictionary["shopify-connections-empty"][language]}
            </div>
          ) : filteredConnections.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 py-6 text-center text-sm text-gray-400">
              {dictionary["shopify-connections-no-results"][language]}
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {needsAttention.length > 0 && (
                <>
                  <GroupHeading
                    icon={<AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
                    label={dictionary["shopify-connections-group-attention"][language]}
                    count={needsAttention.length}
                  />
                  {needsAttention.map(renderConnection)}
                </>
              )}
              {active.length > 0 && (
                <>
                  <GroupHeading
                    icon={<CheckCircle2 className="h-3.5 w-3.5 text-[#22a06b]" />}
                    label={dictionary["shopify-connections-group-active"][language]}
                    count={active.length}
                  />
                  {active.map(renderConnection)}
                </>
              )}
            </div>
          )}

          {/* Agregar conexión (OAuth) */}
          {formMode === "add" ? (
            renderForm("add")
          ) : (
            <button
              type="button"
              onClick={openAdd}
              style={{ backgroundImage: "linear-gradient(135deg, #7DC242 0%, #3D8C40 60%, #1F7A4C 100%)" }}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
            >
              <Store className="h-[18px] w-[18px]" />
              {dictionary["shopify-connections-add-cta"][language]}
            </button>
          )}

          {formMode === "closed" && (
            <div className="mt-auto pt-4">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                {dictionary["close"][language]}
              </button>
            </div>
          )}
        </div>

        {/* Right Side - Visual */}
        <div className="hidden md:flex w-[200px] bg-gradient-to-b from-[#f0fdf4] to-white relative items-center justify-center py-6 px-4 border-l border-gray-100 shrink-0">
          <button
            onClick={() => setModalOpen(false)}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 z-10 transition-colors"
          >
            <X size={18} />
          </button>

          <div className="flex flex-col items-center justify-center gap-8 w-full h-full">
            <div className="bg-white rounded-[24px] shadow-[0_4px_20px_rgba(34,160,107,0.15)] border border-[#dcfce7] p-5 w-24 h-24 flex items-center justify-center z-10 relative">
              <img src={FLUXI_LOGO} alt="Fluxi" className="w-full h-auto object-contain" />
            </div>

            <div className="flex flex-col items-center gap-1 text-[#86efac]">
              <ChevronDown strokeWidth={3} size={24} className="animate-[shopifyFade_1.5s_infinite_0ms]" />
              <ChevronDown strokeWidth={3} size={24} className="animate-[shopifyFade_1.5s_infinite_200ms]" />
              <ChevronDown strokeWidth={3} size={24} className="animate-[shopifyFade_1.5s_infinite_400ms]" />
            </div>

            <div className="bg-white rounded-[24px] shadow-sm border border-gray-200 p-3 w-24 h-24 flex items-center justify-center z-10 relative overflow-hidden">
              <img src={SHOPIFY_LOGO} alt="Shopify" className="w-14 h-14 object-contain" />
            </div>

            <style>{`
              @keyframes shopifyFade {
                0%, 100% { opacity: 0.3; transform: translateY(0); }
                50% { opacity: 1; transform: translateY(3px); }
              }
            `}</style>
          </div>
        </div>
      </div>
    </div>

    {confirmDelete && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
          <div className="mb-3 flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50">
              <Trash2 className="h-5 w-5 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">
              {dictionary["shopify-connections-delete-title"][language]}
            </h3>
          </div>
          <p className="mb-2 text-sm text-gray-700">
            <span className="font-medium">{confirmDelete.label?.trim() || confirmDelete.url_store}</span>
          </p>
          {(confirmDelete.funnels_count ?? 0) > 0 && (
            <p className="mb-2 text-sm text-gray-700">
              <span className="font-semibold">
                {confirmDelete.funnels_count}{" "}
                {confirmDelete.funnels_count === 1
                  ? dictionary["shopify-connections-funnels-one"][language]
                  : dictionary["shopify-connections-funnels-other"][language]}
              </span>{" "}
              {dictionary["shopify-connections-delete-uses"][language]}
            </p>
          )}
          <p className="mb-5 text-sm text-gray-500">
            {dictionary["shopify-connections-delete-warn"][language]}
          </p>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirmDelete(null)}
              className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
            >
              {dictionary["shopify-connections-cancel"][language]}
            </button>
            <button
              type="button"
              onClick={() => {
                const id = confirmDelete.id;
                setConfirmDelete(null);
                handleDelete(id);
              }}
              className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4" />
              {dictionary["shopify-connections-delete-confirm"][language]}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default ModalShopifyConnections;
